import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { api, getToken } from "./lib/api";
import Shell from "./components/Shell";
import Home from "./pages/Home";
import SignIn from "./pages/SignIn";
import Projects from "./pages/Projects";
import NewProject from "./pages/NewProject";
import Project from "./pages/Project";
import Billing from "./pages/Billing";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";

/** Gates the console on a session the SERVER agrees is valid.
 *
 *  Checking only for a token in localStorage would let a revoked or expired
 *  session render the whole shell and then fail every request inside it, which
 *  reads as "the app is broken" rather than "you are signed out". */
function Gate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"checking" | "in" | "out">(getToken() ? "checking" : "out");

  useEffect(() => {
    if (state !== "checking") return;
    api.auth
      .me()
      .then(() => setState("in"))
      .catch(() => setState("out"));
  }, [state]);

  if (state === "checking") return null;
  if (state === "out") return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/signin" element={<SignIn />} />

      {/* The console. Everything the customer's work lives in sits under
          /app, so the marketing page can own the root without a redirect
          dance and a signed-out visitor never sees a flash of the shell. */}
      <Route
        path="/app/*"
        element={
          <Gate>
            <Shell>
              <Console />
            </Shell>
          </Gate>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Console() {
  return (
    <Routes>
      <Route path="/" element={<Projects />} />
      <Route path="/new" element={<NewProject />} />
      <Route path="/projects/:id" element={<Project />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
